import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Loader2 } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { apiProjectsList } from "@/lib/api";

import PortalNav from "@/components/portal/PortalNav";
import GridBackground from "@/components/primitives/GridBackground";
import BottomTabNav from "@/components/portal/BottomTabNav";
import ProjectCard from "@/components/portal/projects/ProjectCard";
import NewProjectModal from "@/components/portal/projects/NewProjectModal";

const FILTERS = [
    { id: "",         label: "All" },
    { id: "active",   label: "Active" },
    { id: "paused",   label: "Paused" },
    { id: "completed",label: "Completed" },
    { id: "archived", label: "Archived" },
];

export default function Projects() {
    const { user, loading: authLoading, openAuthModal } = useAuth();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => { if (!authLoading && !user) openAuthModal("login"); }, [authLoading, user, openAuthModal]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            setLoading(true);
            try { setItems((await apiProjectsList(filter || undefined)).projects || []); } catch { /* offline */ }
            setLoading(false);
        })();
    }, [user, filter]);

    if (!user) return <div className="min-h-screen flex items-center justify-center bg-tca-deep"><Loader2 className="text-tca-cyan animate-spin" /></div>;

    return (
        <div data-testid="projects-page" className="relative min-h-screen bg-tca-deep text-tca-ink overflow-x-hidden pb-20 lg:pb-0">
            <GridBackground fine />
            <PortalNav />

            <main className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 py-8 sm:py-12">
                <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <div className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/85">// active projects</div>
                    <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
                        <h1 className="font-display text-[40px] sm:text-[56px] leading-[1.0] tracking-[-0.025em]">
                            Your Active <em className="not-italic neon-cyan">Builds</em>.
                        </h1>
                        <button
                            type="button" onClick={() => setModalOpen(true)}
                            data-testid="new-project-btn"
                            className="inline-flex items-center gap-2 font-mono-tight font-semibold uppercase tracking-[0.18em] text-xs py-3 px-5 border border-tca-cyan text-tca-cyan-hi bg-gradient-to-b from-[rgba(0,219,255,0.22)] to-[rgba(0,219,255,0.06)] hover:text-white hover:shadow-glow-cyan-lg rounded-full transition-all"
                        >
                            <Plus size={13} /> New Project
                        </button>
                    </div>
                </motion.section>

                <div className="mt-8 flex gap-2 flex-wrap" data-testid="projects-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id || "all"} type="button" onClick={() => setFilter(f.id)}
                            data-testid={`projects-filter-${f.id || "all"}`}
                            className={`font-mono-tight text-[10px] uppercase tracking-[0.22em] px-4 py-1.5 rounded-full border transition-all ${filter === f.id ? "border-tca-cyan text-tca-cyan-hi bg-tca-cyan/10" : "border-tca-cyan/22 text-tca-ink-muted hover:text-tca-cyan hover:border-tca-cyan/55"}`}
                        >{f.label}</button>
                    ))}
                </div>

                <div className="mt-8" data-testid="projects-grid">
                    {loading ? (
                        <div className="flex justify-center py-16"><Loader2 className="text-tca-cyan animate-spin" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-20 text-center">
                            <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/65">// no active builds</div>
                            <p className="mt-3 text-tca-ink-muted">Start your first build — the ecosystem will track it.</p>
                            <button onClick={() => setModalOpen(true)} className="mt-6 font-mono-tight text-[11px] uppercase tracking-[0.22em] py-3 px-5 border border-tca-cyan text-tca-cyan-hi rounded-full">
                                Initialize first project
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {items.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
                        </div>
                    )}
                </div>
            </main>

            <BottomTabNav />
            <NewProjectModal open={modalOpen} onOpenChange={setModalOpen} onCreated={(p) => setItems((it) => [p, ...it])} />
        </div>
    );
}
